import { useEffect, useState, useCallback } from "react";
import AdminLayout from "../components/AdminLayout";
import LoadingButton from "../components/LoadingButton";
import { Plus, CalendarCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, formatRupiah, formatDate } from "../lib/api";

export default function AdminAngsuran() {
  const [rows, setRows] = useState([]);
  const [pinjaman, setPinjaman] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ pinjaman_id: "", amount: "", payment_date: "", note: "" });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const [a, p] = await Promise.all([
        api.get("/admin/angsuran"),
        api.get("/admin/pinjaman", { params: { status: "active" } }),
      ]);
      setRows(a.data);
      setPinjaman(p.data);
    } catch (e) { setError(formatApiError(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const payload = { ...form, amount: parseFloat(form.amount) };
      if (!payload.payment_date) delete payload.payment_date;
      await api.post("/admin/angsuran", payload);
      toast.success("Angsuran berhasil dicatat");
      setOpen(false);
      setForm({ pinjaman_id: "", amount: "", payment_date: "", note: "" });
      load();
    } catch (e) { setError(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight text-brand-ink">Angsuran</h1>
            <p className="text-brand-muted text-sm mt-1">Catat pembayaran angsuran pinjaman anggota.</p>
          </div>
          <button onClick={() => setOpen(true)} data-testid="open-angsuran-form-btn" className="rounded-full bg-brand text-white px-5 py-2.5 text-sm flex items-center gap-2 hover:bg-brand-hover transition">
            <Plus size={14} /> Input Angsuran
          </button>
        </div>

        {error && <div className="rounded-2xl bg-red-50 border border-red-200 text-red-600 text-sm px-4 py-3">{error}</div>}

        <div className="bg-white rounded-3xl border border-brand/10 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="angsuran-table">
              <thead className="bg-brand-soft/60 text-left text-brand-muted">
                <tr>
                  <th className="px-4 py-3">Tanggal</th>
                  <th className="px-4 py-3">Pinjaman ID</th>
                  <th className="px-4 py-3 text-right">Jumlah</th>
                  <th className="px-4 py-3">Catatan</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? <tr><td colSpan={4} className="px-4 py-10 text-center text-brand-muted">Belum ada angsuran</td></tr>
                : rows.map((r) => (
                  <tr key={r.id} className="border-t border-brand/10 hover:bg-brand-soft/30">
                    <td className="px-4 py-3">{formatDate(r.payment_date)}</td>
                    <td className="px-4 py-3 text-xs text-brand-muted">{r.pinjaman_id}</td>
                    <td className="px-4 py-3 text-right font-semibold text-brand-ink">{formatRupiah(r.amount)}</td>
                    <td className="px-4 py-3 text-brand-muted">{r.note || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-brand-ink/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-3" onClick={() => setOpen(false)}>
          <form onSubmit={submit} className="bg-white rounded-3xl w-full sm:max-w-md p-5 sm:p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="angsuran-form">
            <div className="font-heading font-bold text-lg text-brand-ink flex items-center gap-2"><CalendarCheck size={18} /> Input Angsuran</div>
            <Field label="Pinjaman">
              <select required value={form.pinjaman_id} onChange={(e) => setForm((p) => ({ ...p, pinjaman_id: e.target.value }))} className="inp" data-testid="angsuran-pinjaman-select">
                <option value="">— Pilih pinjaman aktif —</option>
                {pinjaman.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.member_name} — sisa {formatRupiah(p.remaining_amount)}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Jumlah (Rp)"><input type="number" min="0" required className="inp" value={form.amount} onChange={(e) => setForm((p) => ({ ...p, amount: e.target.value }))} data-testid="angsuran-amount-input" /></Field>
            <Field label="Tanggal"><input type="date" className="inp" value={form.payment_date} onChange={(e) => setForm((p) => ({ ...p, payment_date: e.target.value }))} data-testid="angsuran-date-input" /></Field>
            <Field label="Catatan"><input className="inp" value={form.note} onChange={(e) => setForm((p) => ({ ...p, note: e.target.value }))} data-testid="angsuran-note-input" /></Field>

            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setOpen(false)} disabled={submitting} className="rounded-full border border-brand/20 px-4 py-2 text-sm disabled:opacity-60">Batal</button>
              <LoadingButton type="submit" loading={submitting} loadingText="Menyimpan…" data-testid="angsuran-submit-btn" className="rounded-full bg-brand text-white px-5 py-2 text-sm hover:bg-brand-hover">Simpan</LoadingButton>
            </div>
            <style>{`.inp{width:100%;border-radius:12px;border:1px solid rgba(11,110,110,0.2);padding:9px 12px;font-size:14px;background:white;outline:none}.inp:focus{box-shadow:0 0 0 3px rgba(11,110,110,0.15);border-color:rgba(11,110,110,0.4)}`}</style>
          </form>
        </div>
      )}
    </AdminLayout>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs font-semibold text-brand-ink mb-1.5 uppercase tracking-[0.16em]">{label}</div>
      {children}
    </label>
  );
}
